import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useCurrentUser } from './UserProvider';
import { loader } from 'graphql.macro';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import CustomSpinner from '../components/CustomSpinner';
import { showToast } from '../graphql/cache/modal';
import { defaultWorkspaceLogo } from '../utilities';
import usePreloadImages from '../hooks/usePreloadImages';

const workspacesQuery = loader('./WorkspaceProvider.workspace.graphql');
const permissionsQuery = loader('./WorkspaceProvider.permissions.graphql');
const assetsQuery = loader('./WorkspaceProvider.assets.graphql');
const updateUserWorkspaceMutation = loader(
  './WorkspaceProvider.update.graphql',
);

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
  const { user, isAdmin, isSupport, defaultObservationWorkspaceId, syncing } =
    useCurrentUser();
  const { pathname } = useLocation();
  const isOnline = useOnlineStatus();
  const isWorkspaceRoute = pathname.startsWith('/workspace');
  const fromUrl = pathname.split('/')[2];
  const workspaceId = isWorkspaceRoute
    ? parseInt(fromUrl)
    : user.lastWorkspaceId;
  const [updateLastWorkspaceId] = useMutation(updateUserWorkspaceMutation);

  const {
    networkStatus,
    refetch: refetchWorkspace,
    data: { workspaces: fetchedWorkspaces = [] } = {},
  } = useQuery(workspacesQuery, {
    fetchPolicy: isOnline ? 'cache-and-network' : 'cache-only',
    nextFetchPolicy: 'cache-first',
  });
  const loadingWorkspaces = networkStatus === NetworkStatus.loading;

  const {
    refetch: refetchWorkspacePermission,
    networkStatus: loadingPermissions,
    data: { workspacePermissions: fetchedPermissions = [] } = {},
  } = useQuery(permissionsQuery, {
    skip: syncing,
    fetchPolicy: isOnline ? 'cache-and-network' : 'cache-only',
  });

  const {
    refetch: refetchWorkspaceVehicle,
    data: { vehicles: fetchedAssets = [] } = {},
  } = useQuery(assetsQuery, {
    skip: syncing,
    fetchPolicy: isOnline ? 'cache-and-network' : 'cache-only',
    nextFetchPolicy: 'cache-first',
  });

  usePreloadImages([
    ...fetchedWorkspaces.map((w) => w.logoUrl),
    ...fetchedAssets.map((a) => a.imageUrl),
  ]);

  const allWorkspaces = useMemo(
    () =>
      fetchedWorkspaces?.map((w) => ({
        ...w,
        logoUrl: w.logoUrl || defaultWorkspaceLogo,
        id: w.id,
        label: w.title || 'Workspace',
        value: w.id,
      })),
    [fetchedWorkspaces],
  );

  const workspaces = useMemo(
    () =>
      fetchedWorkspaces
        ?.map((workspace) => {
          const permissions = fetchedPermissions?.filter(
            ({ workspaceId }) => workspaceId === workspace?.id,
          );

          const usersInWorkspace = permissions?.map(({ userId }) => userId);

          if (usersInWorkspace?.includes(user?.id)) {
            return {
              ...workspace,
              logoUrl: workspace.logoUrl || defaultWorkspaceLogo,
              id: workspace.id,
              label: workspace.title || 'Workspace',
              value: workspace.id,
            };
          } else {
            return false;
          }
        })
        .filter(Boolean),
    [fetchedWorkspaces, user, fetchedPermissions],
  );
  const notInWorkspaces = useMemo(
    () =>
      fetchedWorkspaces
        ?.map((workspace) => {
          const permissions = fetchedPermissions?.filter(
            ({ workspaceId }) => workspaceId === workspace?.id,
          );

          const usersInWorkspace = permissions?.map(({ userId }) => userId);

          if (!usersInWorkspace?.includes(user?.id)) {
            return workspace;
          } else {
            return false;
          }
        })
        .filter(Boolean),
    [fetchedWorkspaces, user, fetchedPermissions],
  );

  const workspace = useMemo(() => {
    const workspaceVehicles = (fetchedAssets ?? [])
      .filter(({ workspaces }) =>
        workspaces.some(({ id }) => id === workspaceId),
      )
      .sort((a, b) => {
        const aUnit = a.unitNumber?.length ? a.unitNumber : null;
        const bUnit = b.unitNumber?.length ? b.unitNumber : null;

        if (aUnit == null && bUnit == null) return 0;
        if (aUnit == null) return 1;
        if (bUnit == null) return -1;

        return aUnit.localeCompare(bUnit, undefined, { sensitivity: 'base' });
      });

    return {
      ...allWorkspaces?.find((workspace) => workspace.id === workspaceId),
      workspaceVehicles: workspaceVehicles ?? [],
    };
  }, [workspaceId, fetchedAssets, allWorkspaces]);

  const workspacePermissions = useMemo(
    () =>
      fetchedPermissions?.filter(({ workspaceId: id }) => id === workspaceId),
    [workspaceId, fetchedPermissions],
  );

  const { observationTemplate } = useMemo(() => {
    return defaultObservationWorkspaceId
      ? allWorkspaces?.find((w) => w.id === defaultObservationWorkspaceId) ?? {}
      : workspace || {};
  }, [allWorkspaces, defaultObservationWorkspaceId, workspace]);

  const isWorkspaceAdmin =
    isAdmin ||
    (workspacePermissions?.length &&
      workspacePermissions?.some(
        (u) => u.userId === user.id && u.accessLevel === 'ADMIN',
      ));
  // Admin of any workspace, not just the current one, for UI that shouldn't
  // change as the user switches workspaces (e.g. the Users sidebar link)
  const isAnyWorkspaceAdmin =
    isAdmin ||
    !!fetchedPermissions?.some(
      (u) => u.userId === user.id && u.accessLevel === 'ADMIN',
    );
  const validUrlUser =
    !isNaN(fromUrl) &&
    !isAdmin &&
    workspaces?.map((w) => w.id)?.some((id) => id === workspaceId);

  const validUrlAdmin =
    !isNaN(fromUrl) &&
    isAdmin &&
    allWorkspaces?.map((w) => w.id)?.some((id) => id === workspaceId);

  const validUrl = isAdmin ? validUrlAdmin : validUrlUser;
  useEffect(() => {
    if (
      validUrl &&
      !!isWorkspaceRoute &&
      user?.lastWorkspaceId !== workspaceId &&
      isOnline
    ) {
      updateLastWorkspaceId({
        variables: { id: user?.id, lastWorkspaceId: workspaceId },
      }).then(() => {
        showToast({
          title: 'Workspace Change',
          message: `You are now in ${workspace?.title}`,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pathname]);

  const workspaceLocations = useMemo(
    () => (!!workspace?.locations ? workspace?.locations?.split('|') : []),
    [workspace?.locations],
  );

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        isWorkspaceAdmin,
        isAnyWorkspaceAdmin,
        availableWorkspaces:
          isAdmin || isSupport ? allWorkspaces : workspaces || [],
        workspaceId,
        refetchWorkspace,
        refetchWorkspacePermission,
        refetchWorkspaceVehicle,
        validUrl,
        loading:
          loadingWorkspaces || loadingPermissions === NetworkStatus.loading,
        inWorkspaces: workspaces || [],
        notInWorkspaces: isAdmin ? notInWorkspaces : [],
        workspaceVehicles: workspace?.workspaceVehicles || [],
        isWorkspaceRoute,
        observationTemplate,
        hasDefaultTemplate: !!defaultObservationWorkspaceId,
        allWorkspaces: allWorkspaces || [],
        workspacePermissions,
        companyPermissions: fetchedPermissions || [],
        allVehicles: fetchedAssets || [],
        syncing,
        workspaceLocations,
      }}
    >
      {loadingWorkspaces && <CustomSpinner text={'Loading Workspace...'} />}
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
